import { economyConfig } from '../config/economyConfig';
import type { ParkingLot } from '../models/ParkingLot';
import { clamp, lerp } from '../utils/math';
import { StaffEngine } from './StaffEngine';

export interface SatisfactionFactors {
  price: number;
  wait: number;
  cleanliness: number;
  security: number;
  availability: number;
  payment: number;
}

export type Rating = 'Excellent' | 'Good' | 'Average' | 'Poor' | 'Bad';

/**
 * ReputationEngine (spec section 16): blends the factors a customer cares about
 * into a satisfaction score, then drifts the lot's reputation toward it so it
 * reflects sustained performance. Reputation feeds back into demand
 * (DemandEngine.reputationMultiplier). Pure & config-driven.
 */
export const ReputationEngine = {
  /** Only overpricing (above market) hurts price satisfaction. */
  priceSatisfaction(price: number): number {
    const market = economyConfig.AVERAGE_MARKET_PRICE;
    const over = Math.max(0, (price - market) / market);
    return clamp(100 - over * economyConfig.REPUTATION.PRICE_SAT_SLOPE, 0, 100);
  },

  /** <= GREAT minutes = 100, >= BAD minutes = 0, linear in between. */
  waitSatisfaction(avgWaitMinutes: number): number {
    const { WAIT_GREAT_MIN, WAIT_BAD_MIN } = economyConfig.REPUTATION;
    if (avgWaitMinutes <= WAIT_GREAT_MIN) return 100;
    if (avgWaitMinutes >= WAIT_BAD_MIN) return 0;
    const t =
      (avgWaitMinutes - WAIT_GREAT_MIN) / (WAIT_BAD_MIN - WAIT_GREAT_MIN);
    return lerp(100, 0, t);
  },

  availabilitySatisfaction(rejectionRate: number): number {
    return clamp(100 - rejectionRate * 100, 0, 100);
  },

  paymentSatisfaction(lot: ParkingLot): number {
    const { PAY_SAT_BASE, PAY_SAT_PER_THROUGHPUT } = economyConfig.REPUTATION;
    const throughput = 1 / StaffEngine.paymentTimeFactor(lot); // >= 1
    return clamp(PAY_SAT_BASE + (throughput - 1) * PAY_SAT_PER_THROUGHPUT, 0, 100);
  },

  factors(lot: ParkingLot): SatisfactionFactors {
    return {
      price: this.priceSatisfaction(lot.parkingPrice),
      wait: this.waitSatisfaction(lot.avgWaitMinutes),
      cleanliness: clamp(lot.cleanliness, 0, 100),
      security: clamp(lot.security, 0, 100),
      availability: this.availabilitySatisfaction(lot.rejectionRateEma),
      payment: this.paymentSatisfaction(lot),
    };
  },

  computeSatisfaction(lot: ParkingLot): number {
    const f = this.factors(lot);
    const w = economyConfig.REPUTATION.WEIGHTS;
    return clamp(
      f.price * w.price +
        f.wait * w.wait +
        f.cleanliness * w.cleanliness +
        f.security * w.security +
        f.availability * w.availability +
        f.payment * w.payment,
      0,
      100
    );
  },

  /** One day of reputation movement toward current satisfaction. */
  updateDaily(lot: ParkingLot): void {
    const satisfaction = this.computeSatisfaction(lot);
    lot.customerSatisfaction = satisfaction;
    const drift = economyConfig.REPUTATION.DRIFT_RATE;
    lot.reputation = clamp(
      lot.reputation + (satisfaction - lot.reputation) * drift,
      economyConfig.REPUTATION_MIN,
      economyConfig.REPUTATION_MAX
    );
  },

  rating(reputation: number): Rating {
    if (reputation >= 90) return 'Excellent';
    if (reputation >= 70) return 'Good';
    if (reputation >= 50) return 'Average';
    if (reputation >= 30) return 'Poor';
    return 'Bad';
  },
};
